/* eslint-disable no-console */
import {
  Container,
  Typography,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Card,
  CardContent,
  IconButton,
  Chip,
  Button,
  Tooltip,
  Stack,
} from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import { fetchStatuses } from '../../../constants/fetchStatuses';
import { isDispatchApiSuccess } from '../../../utils/api';
import { useAppDispatch } from '../../../redux/hooks';
import { companySelector } from '../../../redux/modules/b2bThriftPlan/companies/company.selectors';
import { GetAttachmentResponse } from '../../../types/b2tRatibiPlus';
import {
  deleteCompanyDocument,
  fetchCompanyDocuments,
  fetchDocumentType,
  getAttachment,
  updateCompanyDocument,
  uploadCompanyDocument,
} from '../../../redux/modules/b2bThriftPlan/companies/company.actions';

type UploadCompanyDocumentProps = {
  onChanged?: () => void;
};

const UploadCompanyDocument = ({ onChanged }: UploadCompanyDocumentProps) => {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { documentTypes, companyDocuments, deleteDocumentStatus, deletingDocumentId } = useSelector(companySelector);

  // Determine mode based on companyDocuments length (not id, since id always exists)
  const isEditMode = companyDocuments && companyDocuments.length > 0;

  type UploadRow = {
    id: string;
    documentTypeId: string;
    files: File[];
    existingId?: number;
    existingFilename?: string;
    existingSize?: number;
  };
  const initialRowIdRef = React.useRef<string>(crypto.randomUUID());
  const hasInitializedRef = React.useRef<boolean>(false);
  const previousModeRef = React.useRef<boolean>(false);
  const [rows, setRows] = useState<UploadRow[]>([{ id: initialRowIdRef.current, documentTypeId: '', files: [] }]);
  const previousDocumentsCountRef = React.useRef<number>(0);
  const originalDocTypeMapRef = React.useRef<Map<number, string>>(new Map());

  // Validation: for new uploads, need type + file. For existing, can submit updates
  const hasInvalidRow = rows.some((row) => !row.existingId && row.files.length > 0 && !row.documentTypeId);
  const hasChanges = React.useMemo(() => {
    const hasNewUploads = rows.some((row) => !row.existingId && row.documentTypeId && row.files.length > 0);
    const hasUpdatedTypes = rows.some((row) => {
      if (!row.existingId || !row.documentTypeId) return false;
      const originalDocType = originalDocTypeMapRef.current.get(row.existingId);
      if (originalDocType === undefined) return false;
      return originalDocType !== row.documentTypeId;
    });
    return hasNewUploads || hasUpdatedTypes;
  }, [rows, companyDocuments]);
  const isSubmitDisabled = hasInvalidRow || !hasChanges;
  const getCompanyDocuments = async () => {
    const companyId = Number(id);
    if (!Number.isNaN(companyId)) {
      dispatch(fetchCompanyDocuments(companyId));
    }
  };
  const addRow = () => setRows((prev) => [...prev, { id: crypto.randomUUID(), documentTypeId: '', files: [] }]);
  const removeRow = (rowId: string) =>
    setRows((prev) => (rowId === initialRowIdRef.current ? prev : prev.filter((row) => row.id !== rowId)));

  const updateRowType = (rowId: string, value: string) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.id !== rowId) return row;
        return { ...row, documentTypeId: value };
      }),
    );
  };

  const updateRowFiles = (rowId: string, fileList: FileList | null) =>
    setRows((prev) =>
      prev.map((row) => {
        if (row.id !== rowId) return row;
        const files = fileList ? Array.from(fileList) : [];
        if (row.existingId && fileList && fileList.length > 0 && onChanged) {
          onChanged?.();
        }
        return { ...row, files };
      }),
    );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const companyId = Number(id);
    if (!companyId || Number.isNaN(companyId)) return;

    let hasAnySuccess = false;

    for (const row of rows) {
      if (row.existingId) {
        // Handle document type updates for existing documents
        const originalDoc = companyDocuments?.find((d) => d.id === row.existingId);
        if (originalDoc && row.documentTypeId && String(originalDoc.documentTypeId) !== row.documentTypeId) {
          const res = await dispatch(
            updateCompanyDocument({
              id: row.existingId,
              documentTypeId: Number(row.documentTypeId),
            }),
          );
          if (isDispatchApiSuccess(res)) {
            hasAnySuccess = true;
            enqueueSnackbar('Document type updated successfully', { variant: 'success' });
          } else {
            const errorData = (res as any)?.payload || (res as any)?.error || res;
            const errorMessage = errorData?.message || 'Document update failed';
            enqueueSnackbar(`${row.existingFilename || 'Document'}: ${errorMessage}`, { variant: 'error' });
          }
        }
        continue;
      }

      // Add mode: upload new files
      if (!row.documentTypeId || row.files.length === 0) continue;

      const successFiles: string[] = [];
      const failedFiles: { name: string; error: string }[] = [];

      for (const file of row.files) {
        const res = await dispatch(uploadCompanyDocument({ companyId, file, documentTypeId: row.documentTypeId }));
        if (isDispatchApiSuccess(res)) {
          hasAnySuccess = true;
          successFiles.push(file.name);
        } else {
          const errorData = (res as any)?.payload || (res as any)?.error || res;
          const errorMessage = errorData?.message || 'Upload failed';
          failedFiles.push({ name: file.name, error: errorMessage });
        }
      }

      // Toast persists even if page navigates
      const parts: string[] = [];
      if (successFiles.length > 0) {
        parts.push(`${successFiles.join(', ')} uploaded successfully`);
      }
      if (failedFiles.length > 0) {
        const failMsgs = failedFiles.map((f) => `${f.name}: ${f.error}`);
        parts.push(failMsgs.join('; '));
      }
      if (parts.length > 0) {
        const hasError = failedFiles.length > 0;
        const message = parts.length > 1 ? parts.join(' | ') : parts[0];
        enqueueSnackbar(message, { variant: hasError ? 'error' : 'success' });
      }
    }

    if (hasAnySuccess) {
      setTimeout(() => getCompanyDocuments(), 500);
      if (onChanged) onChanged();
    }
  };

  useEffect(() => {
    dispatch(fetchDocumentType());
  }, [dispatch]);

  // Fetch company documents on mount if id is available
  useEffect(() => {
    if (id && id !== 'create') {
      getCompanyDocuments();
    }
  }, [id, dispatch]);

  // Initialize rows only when switching modes or on first load
  useEffect(() => {
    // Only initialize if mode changed or haven't initialized yet
    const modeChanged = previousModeRef.current !== isEditMode;
    const currentDocumentsCount = companyDocuments?.length || 0;
    const documentsCountChanged = previousDocumentsCountRef.current !== currentDocumentsCount;

    if (!hasInitializedRef.current || modeChanged) {
      if (isEditMode && companyDocuments?.length > 0) {
        // Edit mode: map existing documents to rows
        const mapped: UploadRow[] = companyDocuments.map((doc) => ({
          id: crypto.randomUUID(),
          documentTypeId: String(doc.documentTypeId),
          files: [],
          existingId: doc.id,
          existingFilename: doc.attachment.filename,
          existingSize: doc.attachment.sizeInByte,
        }));
        setRows(mapped);
        originalDocTypeMapRef.current = new Map(companyDocuments.map((doc) => [doc.id, String(doc.documentTypeId)]));
        hasInitializedRef.current = true;
        previousModeRef.current = true;
        previousDocumentsCountRef.current = currentDocumentsCount;
      } else if (!isEditMode) {
        // Add mode: ensure at least one empty row (only reset if switching from edit mode)
        if (!hasInitializedRef.current || previousModeRef.current) {
          setRows([{ id: initialRowIdRef.current, documentTypeId: '', files: [] }]);
          hasInitializedRef.current = true;
          previousModeRef.current = false;
          previousDocumentsCountRef.current = 0;
        }
      }
    } else if (isEditMode && companyDocuments?.length > 0) {
      // If already initialized and in edit mode, update rows when companyDocuments changes
      if (documentsCountChanged) {
        // Documents count changed - remap all documents to rows
        // This handles cases where new documents were uploaded (replacing local file rows)
        const mapped: UploadRow[] = companyDocuments.map((doc) => ({
          id: crypto.randomUUID(),
          documentTypeId: String(doc.documentTypeId),
          files: [],
          existingId: doc.id,
          existingFilename: doc.attachment.filename,
          existingSize: doc.attachment.sizeInByte,
        }));
        setRows(mapped);
        originalDocTypeMapRef.current = new Map(companyDocuments.map((doc) => [doc.id, String(doc.documentTypeId)]));
        previousDocumentsCountRef.current = currentDocumentsCount;
      } else {
        // Document count is same, update existing rows with current documentTypeId values
        setRows((currentRows) => {
          const updated = currentRows.map((row, index) => {
            const doc = companyDocuments?.[index];
            if (doc && row.existingId === doc.id && String(doc.documentTypeId) !== row.documentTypeId) {
              return { ...row, documentTypeId: String(doc.documentTypeId) };
            }
            // If row has local files but we have a matching document from server, replace it
            if (doc && !row.existingId && row.files.length > 0) {
              return {
                id: crypto.randomUUID(),
                documentTypeId: String(doc.documentTypeId),
                files: [],
                existingId: doc.id,
                existingFilename: doc.attachment.filename,
                existingSize: doc.attachment.sizeInByte,
              };
            }
            return row;
          });
          // Check if we need to add new documents that don't match existing rows
          if (updated.length < companyDocuments.length) {
            const existingIds = new Set(updated.filter((r) => r.existingId).map((r) => r.existingId));
            const newDocs = companyDocuments.filter((doc) => !existingIds.has(doc.id));
            newDocs.forEach((doc) => {
              updated.push({
                id: crypto.randomUUID(),
                documentTypeId: String(doc.documentTypeId),
                files: [],
                existingId: doc.id,
                existingFilename: doc.attachment.filename,
                existingSize: doc.attachment.sizeInByte,
              });
            });
          }
          return updated;
        });
        originalDocTypeMapRef.current = new Map(companyDocuments.map((doc) => [doc.id, String(doc.documentTypeId)]));
      }
    }
  }, [isEditMode, companyDocuments]);

  const handleDelete = async (companyDocumentId: number) => {
    const res = await dispatch(deleteCompanyDocument(companyDocumentId));
    if (isDispatchApiSuccess(res)) {
      getCompanyDocuments();
      enqueueSnackbar('Document Deleted!', { variant: 'success' });
    } else {
      // Error response structure: { message, data, statusCode, traceId }
      const errorData = (res as any)?.payload || (res as any)?.error || res;
      const errorMessage = errorData?.message || 'Document Delete error!';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
    if (onChanged) onChanged();
  };

  const handleDownload = async (attachmentId: number, filename?: string) => {
    const res = await dispatch(getAttachment(attachmentId));
    if (isDispatchApiSuccess<GetAttachmentResponse>(res)) {
      const payload = (res as any)?.payload;
      if (payload?.data?.signedUrl) {
        const { signedUrl } = payload.data;
        try {
          // Fetch the file as a blob to enable proper download
          const response = await fetch(signedUrl);
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename || 'document';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } catch (error) {
          enqueueSnackbar('Failed to download document!', { variant: 'error' });
        }
      } else {
        enqueueSnackbar('Failed to download document!', { variant: 'error' });
      }
    } else {
      // Error response structure: { message, data, statusCode, traceId }
      const errorData = (res as any)?.payload || (res as any)?.error || res;
      const errorMessage = errorData?.message || 'Failed to download document!';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  return (
    <Container component="main" maxWidth="xl">
      <Box sx={{ pb: 5 }}>
        <Typography variant="h4">{isEditMode ? 'Edit Company Documents' : 'Upload Company Document'}</Typography>
      </Box>
      <Button variant="contained" color="primary" onClick={addRow}>
        Add another document
      </Button>
      {/* <Loader isLoading={companyDocumentsLoading}> */}
      <form onSubmit={handleSubmit}>
        <Grid item xs={12}>
          {rows.map((row, index) => (
            <Card key={row.id} variant="outlined" sx={{ mt: 2, mb: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <FormControl size="small" sx={{ minWidth: 260 }}>
                    <InputLabel id={`doc-type-${row.id}`}>Document Type</InputLabel>
                    <Select
                      labelId={`doc-type-${row.id}`}
                      label="Document Type"
                      value={row.documentTypeId || ''}
                      onChange={(e: SelectChangeEvent<string>) => updateRowType(row.id, e.target.value)}
                      disabled={!documentTypes || documentTypes.length === 0}
                      displayEmpty
                    >
                      {documentTypes && documentTypes.length > 0 ? (
                        documentTypes.map((documentType) => (
                          <MenuItem key={documentType.id} value={String(documentType.id)}>
                            {documentType.type}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem disabled value="">
                          Loading document types...
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>

                  {!row.existingId && (
                    <Tooltip
                      title={!row.documentTypeId ? 'Select document type first' : 'Upload document'}
                      disableInteractive
                    >
                      <span>
                        <IconButton component="label" size="small" disabled={!row.documentTypeId}>
                          <FileUploadIcon />
                          <input
                            hidden
                            multiple
                            accept="*/*"
                            type="file"
                            onChange={(e) => updateRowFiles(row.id, e.target.files)}
                          />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}

                  {(row.id !== initialRowIdRef.current || index !== 0) && !row.existingId && (
                    <IconButton aria-label="remove" color="error" onClick={() => removeRow(row.id)}>
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>
                <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                  {row.existingId ? (
                    <Stack direction="row" spacing={1} justifyContent="space-between" sx={{ width: '100%' }}>
                      <Chip variant="outlined" size="small" label={row.existingFilename} title={row.existingFilename} />
                      <Stack direction="row" spacing={1}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => {
                            if (row.existingId) {
                              const doc = companyDocuments?.find((d) => d.id === row.existingId);
                              const attachmentId = doc?.attachmentId;
                              if (attachmentId !== undefined) {
                                handleDownload(attachmentId, row.existingFilename);
                              }
                            }
                          }}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                        <LoadingButton
                          size="small"
                          color="error"
                          loading={
                            deleteDocumentStatus === fetchStatuses.pending && row.existingId === deletingDocumentId
                          }
                          loadingPosition="end"
                          onClick={() => row.existingId && handleDelete(row.existingId)}
                        >
                          <DeleteIcon fontSize="small" />
                        </LoadingButton>
                      </Stack>
                    </Stack>
                  ) : (
                    row.files.length > 0 && (
                      <>
                        {row.files.map((file) => (
                          <Chip
                            key={`${file.name}-${file.size}-${file.lastModified}`}
                            label={file.name}
                            variant="outlined"
                          />
                        ))}
                      </>
                    )
                  )}
                </Box>
              </CardContent>
            </Card>
          ))}

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 1 }}>
            <Button type="submit" variant="contained" color="primary" disabled={isSubmitDisabled}>
              {isEditMode ? 'Save Changes' : 'Upload'}
            </Button>
          </Box>
        </Grid>
      </form>
      {/* </Loader> */}
    </Container>
  );
};
export default UploadCompanyDocument;
